/**
 * Conquest illustration system.
 *
 * Bespoke bronze-on-onyx line art built from the brand's existing motifs
 * (the 1px bronze rule, hairline grids, ascending marks). No gradients,
 * no fills beyond a faint bronze wash, no characters or mascots.
 * See replit/DESIGN-REFERENCE.md section 7 for the style guide.
 */

const STROKE = "var(--bronze)";
const STROKE_LIT = "var(--bronze-lit)";

function Base({ viewBox = "0 0 240 160", className = "", children, ...rest }) {
  return (
    <svg
      viewBox={viewBox}
      className={`illustration ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* Dashboard: an ascending line crossing a horizon - "where things stand" */
export function IllustrationAscent(props) {
  return (
    <Base viewBox="0 0 240 140" {...props}>
      <line x1="0" y1="112" x2="240" y2="112" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <polyline
        points="20,100 70,100 100,60 140,80 170,32 220,32"
        stroke={STROKE_LIT}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="220" cy="32" r="3" fill={STROKE_LIT} />
      <line x1="170" y1="32" x2="170" y2="112" stroke={STROKE} strokeWidth="1" opacity="0.35" />
      <line x1="100" y1="60" x2="100" y2="112" stroke={STROKE} strokeWidth="1" opacity="0.35" />
    </Base>
  );
}

/* Diagnostic intro: a compass / bearing mark - taking a reading */
export function IllustrationCompass(props) {
  return (
    <Base viewBox="0 0 160 160" {...props}>
      <circle cx="80" cy="80" r="54" stroke={STROKE} strokeWidth="1" />
      <circle cx="80" cy="80" r="30" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="80" y1="10" x2="80" y2="26" stroke={STROKE} strokeWidth="1" />
      <line x1="80" y1="134" x2="80" y2="150" stroke={STROKE} strokeWidth="1" />
      <line x1="10" y1="80" x2="26" y2="80" stroke={STROKE} strokeWidth="1" />
      <line x1="134" y1="80" x2="150" y2="80" stroke={STROKE} strokeWidth="1" />
      <polygon points="80,50 88,80 80,110 72,80" stroke={STROKE_LIT} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="80" cy="80" r="2.5" fill={STROKE_LIT} />
    </Base>
  );
}

/* Diagnostic result: three graduated bands, echoing the meter component */
export function IllustrationBands(props) {
  return (
    <Base viewBox="0 0 240 120" {...props}>
      <line x1="20" y1="20" x2="150" y2="20" stroke={STROKE} strokeWidth="1" opacity="0.4" />
      <line x1="20" y1="20" x2="60" y2="20" stroke={STROKE_LIT} strokeWidth="2" />
      <line x1="20" y1="56" x2="190" y2="56" stroke={STROKE} strokeWidth="1" opacity="0.4" />
      <line x1="20" y1="56" x2="120" y2="56" stroke={STROKE_LIT} strokeWidth="2" />
      <line x1="20" y1="92" x2="220" y2="92" stroke={STROKE} strokeWidth="1" opacity="0.4" />
      <line x1="20" y1="92" x2="180" y2="92" stroke={STROKE_LIT} strokeWidth="2" />
      <line x1="60" y1="8" x2="60" y2="104" stroke={STROKE} strokeWidth="1" opacity="0.25" strokeDasharray="2 3" />
    </Base>
  );
}

/* The Floor: a foundation grid - the minimum viable structure */
export function IllustrationFoundation(props) {
  return (
    <Base viewBox="0 0 240 120" {...props}>
      <line x1="20" y1="100" x2="220" y2="100" stroke={STROKE_LIT} strokeWidth="1.5" />
      <line x1="40" y1="100" x2="40" y2="60" stroke={STROKE} strokeWidth="1" />
      <line x1="90" y1="100" x2="90" y2="40" stroke={STROKE} strokeWidth="1" />
      <line x1="140" y1="100" x2="140" y2="55" stroke={STROKE} strokeWidth="1" />
      <line x1="190" y1="100" x2="190" y2="30" stroke={STROKE} strokeWidth="1" />
      <line x1="40" y1="60" x2="90" y2="40" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="90" y1="40" x2="140" y2="55" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="140" y1="55" x2="190" y2="30" stroke={STROKE} strokeWidth="1" opacity="0.5" />
    </Base>
  );
}

/* Playbook: an open book rendered as two hairline leaves */
export function IllustrationPages(props) {
  return (
    <Base viewBox="0 0 200 130" {...props}>
      <path d="M100 20 L30 32 L30 108 L100 96 Z" stroke={STROKE} strokeWidth="1" />
      <path d="M100 20 L170 32 L170 108 L100 96 Z" stroke={STROKE} strokeWidth="1" />
      <line x1="100" y1="20" x2="100" y2="96" stroke={STROKE_LIT} strokeWidth="1.5" />
      <line x1="42" y1="52" x2="88" y2="46" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="42" y1="68" x2="88" y2="62" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="112" y1="46" x2="158" y2="52" stroke={STROKE} strokeWidth="1" opacity="0.5" />
      <line x1="112" y1="62" x2="158" y2="68" stroke={STROKE} strokeWidth="1" opacity="0.5" />
    </Base>
  );
}

/* Small icon-scale glyphs for the coach session types, one line-mark each */
const SESSION_ICONS = {
  first: (
    <>
      <line x1="24" y1="40" x2="24" y2="10" stroke={STROKE_LIT} strokeWidth="1.5" />
      <path d="M24 10 C14 18, 14 28, 24 24 C34 28, 34 18, 24 10 Z" stroke={STROKE} strokeWidth="1" />
    </>
  ),
  weekly: (
    <>
      <path d="M12 24 A12 12 0 1 1 20 34" stroke={STROKE_LIT} strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="20,27 20,34 13,34" stroke={STROKE_LIT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  reset: (
    <>
      <line x1="24" y1="8" x2="24" y2="40" stroke={STROKE} strokeWidth="1" />
      <line x1="12" y1="40" x2="36" y2="40" stroke={STROKE_LIT} strokeWidth="1.5" />
      <circle cx="24" cy="16" r="6" stroke={STROKE_LIT} strokeWidth="1.5" />
    </>
  ),
  quarter: (
    <>
      <circle cx="24" cy="24" r="15" stroke={STROKE} strokeWidth="1" />
      <line x1="24" y1="24" x2="24" y2="12" stroke={STROKE_LIT} strokeWidth="1.5" />
      <line x1="24" y1="24" x2="33" y2="24" stroke={STROKE_LIT} strokeWidth="1.5" />
    </>
  ),
  open: (
    <>
      <circle cx="16" cy="24" r="3" fill={STROKE_LIT} />
      <circle cx="24" cy="24" r="3" fill={STROKE_LIT} />
      <circle cx="32" cy="24" r="3" fill={STROKE_LIT} />
      <circle cx="24" cy="24" r="15" stroke={STROKE} strokeWidth="1" opacity="0.5" />
    </>
  ),
};

export function IllustrationSessionIcon({ type, className = "" }) {
  return (
    <Base viewBox="0 0 48 48" className={`illustration-icon ${className}`}>
      {SESSION_ICONS[type] || SESSION_ICONS.open}
    </Base>
  );
}
